import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getProcedureServiceById } from "../../services/api";
import { ProcedureService } from "../../types/types";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import { getFileName, resolveMediaUrl } from "../../utils/media";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faTasks,
  faSpinner,
  faFilePdf,
  faDownload,
  faImage,
  faCalendarAlt,
  faFileAlt,
} from "@fortawesome/free-solid-svg-icons";

const ProcedureDetail: React.FC = () => {
  const [procedure, setProcedure] = useState<ProcedureService | null>(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();

  useEffect(() => {
    if (id) {
      fetchProcedure();
    }
  }, [id, language]);

  const fetchProcedure = async () => {
    try {
      setLoading(true);
      const response = await getProcedureServiceById(parseInt(id!));
      setProcedure(response.data);
    } catch (error) {
      console.error("Error fetching procedure:", error);
    } finally {
      setLoading(false);
    }
  };

  // Clean HTML tags from text
  const cleanHtmlTags = (text: string) => {
    if (!text) return "";
    return text
      .replace(/<p><p>/g, "")
      .replace(/<p>/g, "")
      .replace(/<\/p>/g, "\n\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/&nbsp;/g, " ")
      .replace(/<[^>]*>/g, "")
      .trim();
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(language === "ar" ? "ar-EG" : "fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Direction and text alignment
  const getDirectionClass = () => (language === "ar" ? "rtl" : "ltr");
  const getTextAlignClass = () =>
    language === "ar" ? "text-right" : "text-left";

  if (loading) {
    return (
      <div className={`min-h-screen bg-gray-50 ${getDirectionClass()}`}>
        <div className="container mx-auto px-4 py-16">
          <div className="text-center py-16">
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-mauritania-green-dark text-4xl animate-spin mb-4"
            />
            <p className="text-gray-600 text-xl">
              {getTranslation("loading", language)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!procedure) {
    return (
      <div className={`min-h-screen bg-gray-50 ${getDirectionClass()}`}>
        <div className="container mx-auto px-4 py-16">
          <div className="text-center py-20">
            <FontAwesomeIcon
              icon={faTasks}
              className="text-gray-400 text-6xl mb-4"
            />
            <h2 className="text-2xl font-bold text-gray-600 mb-4">
              {language === "ar"
                ? "الإجراء غير موجود"
                : "Procédure non trouvée"}
            </h2>
            <Link
              to="/procedures"
              className="inline-flex items-center px-6 py-3 bg-mauritania-green-dark text-white rounded-lg hover:bg-mauritania-green transition duration-300"
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                className={`mr-2 ${language === "ar" ? "ml-2 rotate-180" : ""}`}
              />
              {language === "ar"
                ? "العودة إلى الإجراءات"
                : "Retour aux procédures"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Get title in current language
  const getTitle = () => {
    if (language === "ar") {
      return procedure.titrearabe || "بدون عنوان";
    } else {
      return procedure.titrefrancais || "Sans titre";
    }
  };

  // Get content in current language
  const getContent = () => {
    const content =
      language === "ar" ? procedure.textarabe : procedure.textfrancais;
    return cleanHtmlTags(content || "");
  };

  // Get file name for download
  const getDownloadName = () => {
    const baseName = getTitle()
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, "_");
    return `${baseName}.pdf`;
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${getDirectionClass()}`}>
      {/* Procedure Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Header with Image */}
            {procedure.image && (
              <div className="relative h-64 md:h-80">
                <img
                  src={resolveMediaUrl(procedure.image)}
                  alt={getTitle()}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-mauritania-green-dark/70 to-transparent"></div>

                <div className="absolute top-6 left-6 right-6">
                  <div className="flex justify-between items-start">
                    <Link
                      to="/procedures"
                      className="inline-flex items-center px-4 py-2 bg-white text-mauritania-green-dark rounded-lg font-semibold hover:bg-gray-100 transition duration-300"
                    >
                      <FontAwesomeIcon
                        icon={faArrowLeft}
                        className={`mr-2 ${
                          language === "ar" ? "ml-2 rotate-180" : ""
                        }`}
                      />
                      {language === "ar"
                        ? "العودة إلى الإجراءات"
                        : "Retour aux procédures"}
                    </Link>

                    {procedure.createdAt && (
                      <div className="flex items-center text-white bg-black/30 px-3 py-1 rounded-full">
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          className="mr-2"
                        />
                        <span>{formatDate(procedure.createdAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center mb-2">
                    <FontAwesomeIcon
                      icon={faTasks}
                      className="text-mauritania-gold text-2xl mr-3"
                    />
                    <span className="text-white bg-mauritania-green-dark/80 px-3 py-1 rounded text-sm">
                      {language === "ar"
                        ? "إجراء / خدمة"
                        : "Procédure / Service"}
                    </span>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white">
                    {getTitle()}
                  </h1>
                </div>
              </div>
            )}

            {!procedure.image && (
              <div className="bg-gradient-to-r from-mauritania-green-dark to-mauritania-green text-white p-8">
                <div className="flex justify-between items-start mb-4">
                  <Link
                    to="/procedures"
                    className="inline-flex items-center px-4 py-2 bg-white text-mauritania-green-dark rounded-lg font-semibold hover:bg-gray-100 transition duration-300"
                  >
                    <FontAwesomeIcon
                      icon={faArrowLeft}
                      className={`mr-2 ${
                        language === "ar" ? "ml-2 rotate-180" : ""
                      }`}
                    />
                    {language === "ar"
                      ? "العودة إلى الإجراءات"
                      : "Retour aux procédures"}
                  </Link>

                  {procedure.createdAt && (
                    <div className="flex items-center text-mauritania-gold">
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                      <span>{formatDate(procedure.createdAt)}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center mb-4">
                  <FontAwesomeIcon
                    icon={faTasks}
                    className="text-mauritania-gold text-2xl mr-3"
                  />
                  <span className="bg-mauritania-gold/20 text-mauritania-gold px-3 py-1 rounded text-sm">
                    {language === "ar" ? "إجراء / خدمة" : "Procédure / Service"}
                  </span>
                </div>
                <h1 className="text-3xl font-bold mb-4">{getTitle()}</h1>
              </div>
            )}

            {/* Content Section */}
            <div className="p-8">
              {/* File Download Card */}
              {procedure.fichier && (
                <div className="mb-8 p-6 bg-gradient-to-r from-mauritania-green-light/20 to-mauritania-green/20 rounded-lg border border-mauritania-green/30">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center">
                      <div className="bg-red-100 p-3 rounded-lg mr-4">
                        <FontAwesomeIcon
                          icon={faFilePdf}
                          className="text-red-600 text-2xl"
                        />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800 mb-1">
                          {language === "ar" ? "الملف المرفق" : "Fichier joint"}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {language === "ar"
                            ? "يمكنك تحميل هذا الملف للاطلاع على التفاصيل الكاملة"
                            : "Vous pouvez télécharger ce fichier pour voir les détails complets"}
                        </p>
                      </div>
                    </div>
                    <a
                      href={resolveMediaUrl(procedure.fichier)}
                      download={getFileName(procedure.fichier) || getDownloadName()}
                      className="inline-flex items-center px-6 py-3 bg-mauritania-green-dark text-white rounded-lg hover:bg-mauritania-green transition duration-300 font-semibold whitespace-nowrap"
                    >
                      <FontAwesomeIcon icon={faDownload} className="mr-2" />
                      {language === "ar"
                        ? "تحميل الملف"
                        : "Télécharger le fichier"}
                    </a>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="prose max-w-none">
                <div className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
                  {getContent() || (
                    <p className="text-gray-500 italic">
                      {language === "ar"
                        ? "لا يوجد وصف متاح لهذا الإجراء."
                        : "Aucune description disponible pour cette procédure."}
                    </p>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-xl font-bold text-mauritania-green-dark mb-4">
                  {language === "ar"
                    ? "معلومات إضافية"
                    : "Informations supplémentaires"}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {procedure.createdAt && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center text-gray-600 mb-2">
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          className="mr-2"
                        />
                        <span className="font-medium">
                          {language === "ar" ? "تاريخ الإضافة" : "Date d'ajout"}
                        </span>
                      </div>
                      <p className="text-gray-800">
                        {formatDate(procedure.createdAt)}
                      </p>
                    </div>
                  )}

                  {procedure.fichier && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center text-gray-600 mb-2">
                        <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
                        <span className="font-medium">
                          {language === "ar" ? "نوع الملف" : "Type de fichier"}
                        </span>
                      </div>
                      <p className="text-gray-800">PDF Document</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Back Button */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <Link
                  to="/procedures"
                  className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition duration-300 font-semibold"
                >
                  <FontAwesomeIcon
                    icon={faArrowLeft}
                    className={`mr-2 ${
                      language === "ar" ? "ml-2 rotate-180" : ""
                    }`}
                  />
                  {language === "ar"
                    ? "عرض جميع الإجراءات"
                    : "Voir toutes les procédures"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProcedureDetail;
